import { Component, ChangeDetectionStrategy, signal, computed, output, effect, input, PLATFORM_ID, inject, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserPlan } from '../../models/user.model';

export const DRAFT_STORAGE_KEY = 'correctify-draft';
export type Banca = 'FCC' | 'Cebraspe' | 'FGV' | 'ENEM';

@Component({
  selector: 'app-essay-editor',
  standalone: true,
  template: `
<div class="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card md:p-8">
  <div class="grid grid-cols-1 gap-4">
    <section class="rounded-xl border border-slate-200/60 bg-slate-50/50 p-4">
      <label for="banca" class="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
        Banca examinadora
      </label>
      <select
        id="banca"
        name="banca"
        [ngModel]="selectedBanca()"
        (ngModelChange)="selectedBanca.set($event)"
        class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
        @for (banca of availableBancas; track banca) {
          <option [value]="banca">{{ banca }}</option>
        }
      </select>
    </section>
  </div>

  <section class="mt-4 rounded-xl border border-slate-200/60 bg-slate-50/50 p-4">
    <label for="tema" class="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
      Tema da redação
    </label>
    <input
      id="tema"
      name="tema"
      type="text"
      [ngModel]="essayTheme()"
      (ngModelChange)="essayTheme.set($event)"
      required
      #themeModel="ngModel"
      class="w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 shadow-sm transition focus:ring-2"
      [class]="(themeModel.invalid && (themeModel.dirty || themeModel.touched) ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-200')"
      placeholder="Digite o tema que você está trabalhando">

    <p class="mt-2 text-xs text-slate-500">O tema agora é sempre preenchido manualmente pelo usuário.</p>
    @if (themeModel.invalid && (themeModel.dirty || themeModel.touched)) {
      <p class="mt-1 text-xs font-medium text-red-600">Informe o tema da redação.</p>
    }
  </section>

  <section class="mt-6 rounded-xl border border-slate-200/60 bg-white p-4">
    <div class="mb-3 flex items-center justify-between gap-3">
      <label for="essay-body" class="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Texto da redação</label>
      <span class="rounded-full border border-slate-200/80 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
        {{ wordCount() }} palavras
      </span>
    </div>

    <textarea
      id="essay-body"
      #essayTextarea
      (input)="onInput($event)"
      [value]="essayText()"
      [attr.maxlength]="maxChars"
      class="h-96 w-full resize-none rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-slate-800 transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200"
      placeholder="Escreva sua redação aqui..."
      [attr.aria-disabled]="isLoading()"
      [disabled]="isLoading()"></textarea>

    <div class="mt-2 flex items-center justify-end text-xs" [class.text-red-600]="charCount() > maxChars - 200" [class.text-slate-400]="charCount() <= maxChars - 200">
      <span>{{ charCount() }} / {{ maxChars }} caracteres</span>
    </div>
  </section>

  <div class="mt-4 space-y-3">
    <div class="flex items-center justify-between text-sm font-medium text-slate-600">
      <span>Mínimo de {{ minLines }} linhas</span>
      <span
        class="rounded-md px-2 py-0.5 font-semibold"
        [class.text-red-600]="!isLineCountValid() && lineCount() > 0"
        [class.bg-red-100]="!isLineCountValid() && lineCount() > 0"
        [class.text-emerald-700]="isLineCountValid()"
        [class.bg-emerald-100]="isLineCountValid()">
        {{ lineCount() }} linhas
      </span>
    </div>

    <div class="h-2.5 w-full rounded-full bg-slate-200">
      <div
        class="h-2.5 rounded-full transition-all duration-300"
        [class.bg-red-500]="!isLineCountValid()"
        [class.bg-blue-600]="isLineCountValid()"
        [style.width.%]="progress()"></div>
    </div>

    @if (essayText().trim().length > 0 && !isLineCountValid()) {
      <p class="pt-1 text-center text-sm text-red-600">
        Sua redação precisa ter no mínimo {{ minLines }} linhas para ser corrigida.
      </p>
    }
  </div>

  <div class="mt-6 flex flex-col items-center justify-between gap-4 md:flex-row">
    <p class="text-sm text-slate-500">
      Revise o tema antes de enviar. A correção segue a banca selecionada.
    </p>

    <button
      (click)="submitEssay()"
      [disabled]="!canSubmit()"
      class="btn-primary inline-flex w-full items-center justify-center gap-2 px-8 py-3 shadow-md shadow-blue-900/20 md:w-auto">
      @if (isLoading()) {
        <div class="loader !h-5 !w-5 !border-2 !border-t-white"></div>
        <span>Analisando...</span>
      } @else {
        <span>Corrigir redação</span>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="m12.75 15 3-3m0 0-3-3m3 3h-7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      }
    </button>
  </div>

  @if (isLoading()) {
    <div class="mt-4 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50/50 p-4">
      <p class="text-sm font-semibold text-blue-900">{{ loadingStatus() }}</p>
      @if (loadingSteps().length > 0) {
        <div class="mt-3 space-y-2">
          @for (step of loadingSteps(); track step) {
            <div class="flex items-center text-sm text-blue-900">
              <svg class="mr-2 h-4 w-4 text-emerald-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 0 1 0 1.42l-7.35 7.35a1 1 0 0 1-1.415 0l-3-3a1 1 0 1 1 1.414-1.414l2.293 2.293 6.643-6.643a1 1 0 0 1 1.415 0Z" clip-rule="evenodd" />
              </svg>
              <span>{{ step }}</span>
            </div>
          }
        </div>
      }
    </div>
  }
</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule]
})
export class EssayEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('essayTextarea') essayTextarea?: ElementRef<HTMLTextAreaElement>;

  essaySubmit = output<{ essay: string; banca: Banca; theme: string; }>();
  isLoading = input<boolean>(false);
  loadingStatus = input<string>('Analisando redação...');
  loadingSteps = input<string[]>([]);
  credits = input<number>(0);
  userPlan = input<UserPlan | undefined>();
  hasFreeCredit = input<boolean>(false);

  private readonly platformId = inject(PLATFORM_ID);

  private getInitialDraft(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(DRAFT_STORAGE_KEY) || '';
    }
    return '';
  }

  readonly essayText = signal<string>(this.getInitialDraft());
  readonly essayTheme = signal<string>('');
  readonly minLines = 10;
  readonly maxChars = 15000;
  readonly selectedBanca = signal<Banca>('FCC');
  readonly availableBancas: Banca[] = ['FCC', 'FGV', 'Cebraspe', 'ENEM'];
  readonly lineCount = signal<number>(0);
  readonly charCount = computed(() => this.essayText().length);
  private resizeHandler?: () => void;
  private pageShowHandler?: () => void;
  private visibilityHandler?: () => void;
  private focusHandler?: () => void;
  private rafId: number | null = null;

  constructor() {
    effect(() => {
      if (this.essayText() === '' && !isPlatformBrowser(this.platformId)) return;

      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(DRAFT_STORAGE_KEY, this.essayText());
      }
    });
  }

  readonly wordCount = computed(() => this.essayText().trim().split(/\s+/).filter(word => word.length > 0).length);
  readonly isLineCountValid = computed(() => this.lineCount() >= this.minLines);

  readonly canSubmit = computed(() => {
    if (
      this.isLoading()
      || !this.isLineCountValid()
      || this.essayTheme().trim() === ''
    ) {
      return false;
    }

    if (this.userPlan() === 'gratuito') {
      return this.hasFreeCredit();
    }

    return this.credits() > 0;
  });

  readonly progress = computed(() => {
    const lines = this.lineCount();
    if (lines <= 0) return 0;
    return Math.min(100, (lines / this.minLines) * 100);
  });

  onInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.essayText.set(textarea.value);
    this.scheduleLineCountRecalc();
  }

  ngAfterViewInit(): void {
    this.scheduleLineCountRecalc();

    if (!isPlatformBrowser(this.platformId)) return;

    this.resizeHandler = () => this.scheduleLineCountRecalc();
    this.pageShowHandler = () => this.scheduleLineCountRecalc();
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.scheduleLineCountRecalc();
      }
    };
    this.focusHandler = () => this.scheduleLineCountRecalc();

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('pageshow', this.pageShowHandler);
    window.addEventListener('focus', this.focusHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnDestroy(): void {
    if (this.rafId !== null && isPlatformBrowser(this.platformId)) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (isPlatformBrowser(this.platformId)) {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      if (this.pageShowHandler) {
        window.removeEventListener('pageshow', this.pageShowHandler);
      }
      if (this.focusHandler) {
        window.removeEventListener('focus', this.focusHandler);
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
    }
  }

  private scheduleLineCountRecalc(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.updateVisualLineCount();
      return;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateVisualLineCount();
    });
  }

  private getLogicalLineCount(text: string): number {
    if (!text.trim()) {
      return 0;
    }

    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    return Math.max(1, normalized.split('\n').length);
  }

  private updateVisualLineCount(): void {
    const text = this.essayText();
    if (!text.trim()) {
      this.lineCount.set(0);
      return;
    }

    const textarea = this.essayTextarea?.nativeElement;
    if (!textarea) {
      this.lineCount.set(this.getLogicalLineCount(text));
      return;
    }

    const style = getComputedStyle(textarea);
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;
    const availableWidth = Math.max(1, textarea.clientWidth - paddingLeft - paddingRight);

    if (availableWidth < 80 || !style.font) {
      this.lineCount.set(this.getLogicalLineCount(text));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.lineCount.set(this.getLogicalLineCount(text));
      return;
    }

    ctx.font = style.font;
    const paragraphs = text.split('\n');
    const lines = paragraphs.reduce((acc, paragraph) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return acc + 1;

      const words = paragraph.split(/\s+/).filter(Boolean);
      if (words.length === 0) return acc + 1;

      let count = 1;
      let currentWidth = 0;
      const spaceWidth = ctx.measureText(' ').width;

      for (const word of words) {
        const wordWidth = ctx.measureText(word).width;
        const nextWidth = currentWidth === 0 ? wordWidth : currentWidth + spaceWidth + wordWidth;

        if (nextWidth <= availableWidth) {
          currentWidth = nextWidth;
          continue;
        }

        if (wordWidth <= availableWidth) {
          count += 1;
          currentWidth = wordWidth;
          continue;
        }

        let remaining = word;
        while (remaining.length > 0) {
          let fit = '';
          for (const char of remaining) {
            const attempt = fit + char;
            if (ctx.measureText(attempt).width > availableWidth) {
              break;
            }
            fit = attempt;
          }

          if (!fit) {
            fit = remaining[0];
          }

          remaining = remaining.slice(fit.length);
          if (remaining.length > 0) {
            count += 1;
          }
        }

        currentWidth = 0;
      }

      return acc + count;
    }, 0);

    const visualLines = Math.max(1, lines);
    this.lineCount.set(visualLines);
  }

  submitEssay() {
    if (this.canSubmit()) {
      this.essaySubmit.emit({
        essay: this.essayText(),
        banca: this.selectedBanca(),
        theme: this.essayTheme().trim()
      });
    }
  }
}
